import { useState } from 'react';

const PAINS = [
  {
    icon: 'ri-money-dollar-circle-line',
    title: '광고비는 나가는데, 매출은 제자리',
    desc: '클릭은 있는데 구매가 없다면,\n타겟팅 전략이 완전히 잘못된 겁니다.',
    stat: '광고주 78%가 경험',
    color: '#FF4D4D',
  },
  {
    icon: 'ri-bar-chart-line',
    title: 'ROAS 2~3배, 여기서 더 안 오름',
    desc: '상위 5% 쇼핑몰 평균 ROAS는 12배.\n지금 놓치고 있는 구조가 분명히 있습니다.',
    stat: '평균 ROAS 12배 가능',
    color: '#FF8C00',
  },
  {
    icon: 'ri-question-line',
    title: '어디서 낭비되는지 모름',
    desc: '어떤 광고에서 돈이 새는지 모른다면,\n지금도 예산을 낭비하고 있는 겁니다.',
    stat: '예산의 평균 40% 낭비',
    color: '#FF4D4D',
  },
  {
    icon: 'ri-repeat-line',
    title: '대행사 바꿨는데 결과는 똑같음',
    desc: '대행사 문제가 아닐 수도 있습니다.\n전략 자체를 완전히 바꿔야 합니다.',
    stat: '대행사 교체 후 동일',
    color: '#FF8C00',
  },
];

export default function DiagnosticSection() {
  const [checked, setChecked] = useState<number[]>([]);

  const toggle = (i: number) =>
    setChecked(p => p.includes(i) ? p.filter(x => x !== i) : [...p, i]);

  const scrollToContact = () =>
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section id="diagnostic" className="py-12 md:py-24 bg-[#060606] relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#FF4D4D]/30 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" />
      <div className="absolute top-1/2 left-1/4 w-[600px] h-[400px] -translate-y-1/2 bg-[#FF4D4D]/4 blur-[160px] pointer-events-none rounded-full" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
        {/* Header */}
        <div className="text-center mb-5 md:mb-4">
          <span className="inline-flex items-center gap-2 bg-[#FF4D4D]/10 border border-[#FF4D4D]/25 text-[#FF4D4D] text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-5 md:mb-6">
            <i className="ri-error-warning-line text-sm"></i>
            광고 낭비 진단
          </span>
        </div>
        <h2 className="text-center font-black text-white mb-3 md:mb-3"
          style={{ fontSize: 'clamp(1.6rem, 6vw, 4rem)', fontFamily: "'Space Grotesk', 'Noto Sans KR', sans-serif", lineHeight: 1.2 }}>
          지금 이 중에<br />
          <span className="text-[#FF4D4D]">해당되는 게 있나요?</span>
        </h2>
        <p className="text-center text-white/35 text-sm leading-relaxed mb-7 md:mb-12">해당 항목을 클릭해보세요</p>

        {/* Checklist grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-7 md:mb-10 max-w-4xl mx-auto">
          {PAINS.map((p, i) => {
            const active = checked.includes(i);
            return (
              <button
                key={i}
                onClick={() => toggle(i)}
                className="text-left rounded-xl md:rounded-2xl p-5 pb-6 md:p-7 md:pb-8 border transition-all duration-200 cursor-pointer relative group"
                style={{
                  background: active ? `${p.color}10` : 'rgba(255,255,255,0.02)',
                  borderColor: active ? `${p.color}50` : 'rgba(255,255,255,0.06)',
                }}
              >
                {/* Checkbox */}
                <div className="absolute top-4 right-4 md:top-5 md:right-5 w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200"
                  style={{
                    borderColor: active ? p.color : 'rgba(255,255,255,0.15)',
                    background: active ? p.color : 'transparent',
                  }}>
                  {active && <i className="ri-check-line text-white text-xs"></i>}
                </div>

                <div className="flex items-start gap-3 md:gap-4 pr-8 md:pr-10">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ background: active ? `${p.color}20` : 'rgba(255,255,255,0.05)' }}>
                    <i className={`${p.icon} text-base md:text-xl`}
                      style={{ color: active ? p.color : 'rgba(255,255,255,0.3)' }}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-white text-sm md:text-base mb-2 md:mb-3 leading-[1.45]">{p.title}</div>
                    <p className="text-white/50 text-xs md:text-sm leading-[1.95] whitespace-pre-line mb-1">{p.desc}</p>
                    <span className="inline-block mt-3 text-xs md:text-sm font-bold px-2.5 md:px-3 py-1 md:py-1.5 rounded-full"
                      style={{
                        background: active ? `${p.color}20` : 'rgba(255,255,255,0.05)',
                        color: active ? p.color : 'rgba(255,255,255,0.25)',
                      }}>
                      {p.stat}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Dynamic CTA */}
        <div className="max-w-4xl mx-auto">
          {checked.length === 0 && (
            <div className="text-center py-5 md:py-8">
              <p className="text-white/25 text-sm leading-relaxed mb-5">
                하나라도 해당된다면,<br className="block md:hidden" />
                <span className="hidden md:inline"> </span>
                지금 무료 진단이 필요합니다
              </p>
              <button
                onClick={scrollToContact}
                className="inline-flex items-center gap-2 border border-[#00FF87]/30 text-[#00FF87] hover:bg-[#00FF87]/10 font-bold px-8 md:px-10 py-3.5 md:py-4 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap text-sm md:text-base w-full sm:w-auto justify-center"
              >
                그래도 진단 받기
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          )}
          {checked.length > 0 && (
            <div className="rounded-xl md:rounded-2xl border border-[#FF4D4D]/25 bg-[#FF4D4D]/5 p-6 md:p-8 text-center">
              <div className="text-[#FF4D4D] font-black text-2xl md:text-3xl mb-3"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {checked.length}개 해당!
              </div>
              <p className="text-white/70 text-sm md:text-base leading-[1.75] mb-6 md:mb-6">
                {checked.length === 1 && (
                  <>
                    이미 수익을 놓치고 있습니다.<br className="block md:hidden" />
                    <br className="hidden md:block" />
                    지금 진단받으세요.
                  </>
                )}
                {checked.length === 2 && (
                  <>
                    광고 전략에{' '}
                    <br className="hidden md:block" />
                    구조적 문제가 있습니다.
                  </>
                )}
                {checked.length === 3 && (
                  <>
                    지금 즉시 광고 구조{' '}
                    <br className="hidden md:block" />
                    전환이 필요합니다.
                  </>
                )}
                {checked.length === 4 && (
                  <>
                    전체 광고 운영 방식을 바꿔야 합니다.<br className="block md:hidden" />
                    <br className="hidden md:block" />
                    지금 바로 시작하세요.
                  </>
                )}
              </p>
              <button
                onClick={scrollToContact}
                className="inline-flex items-center gap-2 md:gap-3 bg-[#00FF87] text-black font-black px-8 md:px-12 py-4 md:py-5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap text-base md:text-lg hover:bg-[#00e87a] w-full sm:w-auto justify-center"
                style={{ boxShadow: '0 0 40px rgba(0,255,135,0.3)' }}
              >
                광고 진단 무료로 받기
                <i className="ri-arrow-right-line text-lg md:text-xl"></i>
              </button>
              <p className="text-white/25 text-xs md:text-sm mt-4 leading-relaxed">
                상담 비용 0원<br className="block md:hidden" />
                <span className="hidden md:inline"> · </span>
                24시간 내 응답
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
